import React, { FC, useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router";
import ConfirmPopup from "../../components/ConfirmPopup/ConfirmPopup";
import HeaderActions from "../../components/HeaderActions/HeaderActions";
import { Page, PageContent, PageHeader } from "../../components/PageLayout";
import useGetPublications from "../../core/api/useGetPublications";
import { getApiLink } from "../../core/apiConfig";
import API from "../../core/enums/API";
import RoutesEnum from "../../core/enums/RoutesEnum";
import { filterDataBySearch } from "../../core/helpers";
import useConfirmPopup from "../../hooks/useConfirmPopup";
import ActionButtons, { ActionButtonTypeEnum } from "../../components/ActionButtons/ActionButtons";
import AddIcon from "@material-ui/icons/Add";
import MUITable, { iTableConfigItem } from "../../components/MUITable/MUITable";
import EditIcon from "@material-ui/icons/Edit";
import ListIcon from "@material-ui/icons/List";
import DeleteIcon from "@material-ui/icons/Delete";
import { highlightValue } from "../../components/MUITable/MUITable.helpers";
import { iShortPublication } from "../../core/models/iPublication";
import PageSubHeader from "../../components/PageLayout/PageSubHeader";
import FormSelect from "../../components/Form/FormSelect";
import { Button, Grid } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import useGetSubscriptionPackages from "../../core/api/useGetSubscriptionPackages";
import useNotification from "../../hooks/useNotifications";
import { filterBySubscriptionPackages, getSubscriptionPackageNames, toTsv } from "./Publications.helpers";

const Publications: FC = () => {
    let history = useHistory();
    const { data, loading, post, response, getPublications } = useGetPublications();
    const subscriptionPackages = useGetSubscriptionPackages();
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedPackageIds, setSelectedPackageIds] = useState<number[]>([]);
    const removeConfirm = useConfirmPopup();
    const { addNotification } = useNotification();

    const filteredData: iShortPublication[] = useMemo(
        () => filterBySubscriptionPackages(filterDataBySearch(data, searchQuery), selectedPackageIds),
        [data, searchQuery, selectedPackageIds],
    );

    const getPackageNames = useCallback(
        (packageIds: number[]) => getSubscriptionPackageNames(packageIds, subscriptionPackages.data),
        [subscriptionPackages.data],
    );

    const handleCopyTable = async () => {
        const tsv = toTsv(
            ['ID', 'Title', 'Date', 'Category', 'Subscription packages'],
            filteredData.map(item => [
                item.id,
                item.title,
                item.createDate,
                item.titleCategoryName,
                getPackageNames(item.subscriptionPackageIds),
            ]),
        );

        try {
            await navigator.clipboard.writeText(tsv);
            addNotification({
                message: <span>{filteredData.length} publications copied to clipboard.</span>,
                status: "success",
            });
        } catch {
            addNotification({
                message: <span>Could not copy the table to clipboard.</span>,
                status: "error",
            });
        }
    };

    const showRemoveConfirm = useCallback(
        (id: number, title: string) => {
            removeConfirm.show(id, title);
        },
        [removeConfirm],
    );

    const handleRemovePublication = async () => {
        await post(getApiLink(API.DELETE_PUBLICATION, { id: removeConfirm.itemId }));

        if (response.ok) {
            getPublications();
        }
    };

    const tableConfig: iTableConfigItem[] = useMemo(() => [
        { title: 'ID', width: 70, Value: ({ index }) => <>{filteredData[index].id}</> },
        { title: 'Title', Value: ({ index }) => <>{highlightValue(filteredData[index].title, searchQuery)}</>},
        { title: 'Date', width: 150, Value: ({ index }) => <>{filteredData[index].createDate}</> },
        { title: 'Category', Value: ({ index }) => <>{filteredData[index].titleCategoryName}</> },
        { title: 'Subscription packages', Value: ({ index }) => <>{getPackageNames(filteredData[index].subscriptionPackageIds)}</> },
        {
            title: '',
            width: 150,
            Value: ({ index }) => {
                const item = filteredData[index];

                return (
                    <ActionButtons config={[
                        {
                            type: ActionButtonTypeEnum.LinkIconButton,
                            ariaLabel: 'Edit',
                            link: RoutesEnum.PublicationEdit.replace(':id', `${item.id}`),
                            Icon: EditIcon,
                        },
                        {
                            type: ActionButtonTypeEnum.LinkIconButton,
                            ariaLabel: 'Manage Chapters',
                            link: RoutesEnum.Chapters.replace(':id', `${item.id}`),
                            Icon: ListIcon,
                        },
                        {
                            ariaLabel: 'Delete',
                            Icon: DeleteIcon,
                            color: 'secondary',
                            onClick: () => showRemoveConfirm(item.id, item.title),
                        },
                    ]}/>
                )
            },
        },
    ], [filteredData, showRemoveConfirm, searchQuery, getPackageNames]);

    return (
        <Page>
            <PageHeader title="Publications">
                <HeaderActions
                    searchQuery={searchQuery}
                    searchLabel="Search publications"
                    setSearchQuery={(value: string) => setSearchQuery(value)}
                    submitIcon={<AddIcon />}
                    submitText="Add publication"
                    onSubmit={() => history.push(RoutesEnum.PublicationAdd)}
                />
            </PageHeader>

            <PageContent>
                <PageSubHeader>
                    <Grid item xs={4}>
                        <FormSelect
                            label="Subscription packages"
                            value={selectedPackageIds}
                            onChange={value => setSelectedPackageIds(value)}
                            options={subscriptionPackages.data}
                            isLoading={subscriptionPackages.loading}
                        />
                    </Grid>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<FileCopyIcon />}
                        onClick={handleCopyTable}
                        disabled={loading || !filteredData.length}
                    >
                        Copy table
                    </Button>
                </PageSubHeader>
                <MUITable isLoading={loading} config={tableConfig} rowsCount={filteredData.length}/>
                <ConfirmPopup
                    title={`Delete publication ${removeConfirm.itemTitle}`}
                    message="Are you sure to delete this item?"
                    shown={removeConfirm.shown}
                    onConfirm={handleRemovePublication}
                    closeModal={removeConfirm.hide}
                    submitButtonLabel="Delete"
                />
            </PageContent>
        </Page>
    );
};

export default Publications;
